// "Language" in the header: opens a dialog that either lets the browser translate the
// page on-device (Chrome 138+/Edge 148+, desktop only), or - when it cannot - shows the
// editor-controlled help text from the CMS. We never hand the visitor off to a
// third-party translation service.
(function () {
    const dialog = document.getElementById('language-dialog');
    const openButton = document.querySelector('.js-language-open');

    if (!dialog || !openButton) {
        return;
    }

    // Every language the Translator API can handle, by its own name plus the Norwegian
    // one so the filter matches whichever the visitor types. The API has no way to list
    // these itself (crbug #68), so the list is maintained here; anything the browser
    // turns out not to support is dropped when we probe it below.

    const picker = dialog.querySelector('.language-dialog__picker');

    // Served by the view from one list in C#, shared with the CMS dropdown, so the two
    // cannot drift apart. Shown in English like the rest of the dialog; the native and
    // Norwegian names ride along so the filter matches whichever a visitor types.
    let LANGUAGES = [];
    try {
        LANGUAGES = JSON.parse(picker.getAttribute('data-languages') || '[]').map(function (l) {
            return { code: l.c, en: l.en, native: l.na, nb: l.nb };
        });
    } catch (e) {
        LANGUAGES = [];
    }
    const help = dialog.querySelector('.language-dialog__help');
    const status = dialog.querySelector('.language-dialog__status');
    const list = dialog.querySelector('.language-dialog__list');
    const search = dialog.querySelector('.language-dialog__search');
    const closeButton = dialog.querySelector('.js-language-close');
    const offButton = dialog.querySelector('.language-dialog__off');
    const helpToggle = dialog.querySelector('.language-dialog__help-toggle');

    const disclaimer = document.querySelector('.language-disclaimer');
    // The API knows "no", not "nb" or "nn" - a Norwegian branch on either of those culture
    // codes would otherwise find no language pairs at all.
    const pageLanguage = (document.documentElement.getAttribute('lang') || 'no').toLowerCase();
    const sourceLanguage = (pageLanguage === 'nb' || pageLanguage === 'nn') ? 'no' : pageLanguage;

    // Remembered across pages so a visitor does not have to pick their language again on
    // every click. Storage can be unavailable (private mode), and that is fine.
    const STORAGE_KEY = 'sk-translate-language';

    function remembered() {
        try {
            return localStorage.getItem(STORAGE_KEY);
        } catch (e) {
            return null;
        }
    }

    function remember(code) {
        try {
            if (code === sourceLanguage) {
                localStorage.removeItem(STORAGE_KEY);
            } else {
                localStorage.setItem(STORAGE_KEY, code);
            }
        } catch (e) {
            // Nothing to do - the choice just will not survive the next page.
        }
    }

    // Nodes whose text is markup or code, not prose, plus the dialog itself - its list
    // must stay readable for someone who cannot read the page.
    const SKIP_TAGS = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE'];
    const SKIP_SELECTOR = '#language-dialog, [translate="no"], .notranslate';
    const PARALLEL_TRANSLATIONS = 8;

    let originals = null;
    let originalPlaceholders = null;
    let checkedAvailability = false;
    // Bumped on every pick. A run that is no longer the latest gives up after each await,
    // so a new pick - or turning translation off - cancels whatever is still in flight.
    let generation = 0;
    let current = sourceLanguage;
    // Set once the browser turns out to have languages to list. Until then the help text
    // is the whole dialog, and there is nothing to switch between.
    let hasPicker = false;

    // The dialog has two faces: the language list, and the help text reached from it by
    // the "?" button. The help text has no "?" of its own - its close button is the way
    // back to the list, and only the list's close button closes the dialog.
    function showHelp(show) {
        if (!hasPicker) {
            return;
        }
        help.hidden = !show;
        picker.hidden = show;
        helpToggle.hidden = show;
        closeButton.setAttribute('aria-label', closeButton.getAttribute(show ? 'data-label-back' : 'data-label-close'));
    }

    // Always lands on the language list, whatever was showing when it was last closed.
    function open() {
        showHelp(false);
        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            dialog.setAttribute('open', '');
        }
        buildList();
    }

    function close() {
        if (typeof dialog.close === 'function') {
            dialog.close();
        } else {
            dialog.removeAttribute('open');
        }
    }

    openButton.addEventListener('click', open);
    closeButton.addEventListener('click', function () {
        if (hasPicker && !help.hidden) {
            showHelp(false);
        } else {
            close();
        }
    });

    // Clicking the backdrop closes: the backdrop is the dialog element itself, the inner
    // box swallows its own clicks.
    dialog.addEventListener('click', function (e) {
        if (e.target === dialog) {
            close();
        }
    });

    // Reapply the remembered language when the visitor navigates. Only when the model is
    // already on the machine: downloading one needs a click, and starting a download
    // behind someone's back would be wrong anyway.
    async function reapplyRemembered() {
        const stored = remembered();

        if (!stored || stored === sourceLanguage || !('Translator' in self)) {
            return;
        }

        try {
            const availability = await Translator.availability({ sourceLanguage: sourceLanguage, targetLanguage: stored });
            if (availability === 'available') {
                await translatePage(stored);
            }
        } catch (e) {
            // Leave the page in its own language.
        }
    }

    reapplyRemembered();

    function setStatus(key) {
        status.textContent = key ? picker.getAttribute('data-status-' + key) || '' : '';
    }

    function label(name) {
        return picker.getAttribute('data-label-' + name) || '';
    }

    // Run the same job over a list a few at a time, so 39 availability probes do not go
    // out as 39 simultaneous calls.
    async function inBatches(items, job) {
        let next = 0;

        async function worker() {
            while (next < items.length) {
                const index = next++;
                await job(items[index], index);
            }
        }

        const workers = [];
        for (let i = 0; i < PARALLEL_TRANSLATIONS; i++) {
            workers.push(worker());
        }
        await Promise.all(workers);
    }

    function languageButton(language) {
        const item = document.createElement('li');
        const button = document.createElement('button');

        button.type = 'button';
        button.textContent = language.en;
        button.setAttribute('data-language', language.code);
        button.setAttribute('data-search', (language.en + ' ' + language.native + ' ' + language.nb + ' ' + language.code).toLowerCase());
        if (language.code === current) {
            button.setAttribute('aria-current', 'true');
        }

        button.addEventListener('click', function () {
            translatePage(language.code);
        });

        item.appendChild(button);
        return item;
    }

    function group(heading, languages) {
        const section = document.createElement('div');
        section.className = 'language-dialog__group';

        const title = document.createElement('h3');
        title.textContent = heading;
        section.appendChild(title);

        const ul = document.createElement('ul');
        languages.forEach(function (language) {
            ul.appendChild(languageButton(language));
        });
        section.appendChild(ul);

        return section;
    }

    // Only offer languages the browser actually has a model for.
    async function buildList() {
        if (checkedAvailability || !('Translator' in self)) {
            return;
        }
        checkedAvailability = true;

        // Decided synchronously, so the help text never flashes before the list appears.
        help.hidden = true;

        const available = [];
        await inBatches(LANGUAGES, async function (language) {
            if (language.code === sourceLanguage) {
                return;
            }
            try {
                const availability = await Translator.availability({ sourceLanguage: sourceLanguage, targetLanguage: language.code });
                if (availability && availability !== 'unavailable') {
                    available.push(language);
                }
            } catch (e) {
                // Treat a failed probe as unsupported.
            }
        });

        if (!available.length) {
            help.hidden = false;
            return;
        }

        const byCode = {};
        available.forEach(function (language) { byCode[language.code] = language; });

        const commonCodes = (picker.getAttribute('data-most-used') || '')
            .split(',')
            .map(function (code) { return code.trim().toLowerCase(); });

        const common = [];
        commonCodes.forEach(function (code) {
            if (byCode[code] && common.indexOf(byCode[code]) === -1) {
                common.push(byCode[code]);
            }
        });

        const onShortlist = {};
        common.forEach(function (language) { onShortlist[language.code] = true; });

        const rest = available
            .filter(function (language) { return !onShortlist[language.code]; })
            .sort(function (a, b) { return a.en.localeCompare(b.en, 'en'); });

        if (common.length) {
            list.appendChild(group(label('common'), common));
        }
        if (rest.length) {
            list.appendChild(group(label('all'), rest));
        }

        offButton.addEventListener('click', function () {
            translatePage(sourceLanguage);
        });

        const noMatch = document.createElement('p');
        noMatch.className = 'language-dialog__nomatch';
        noMatch.textContent = label('nomatch');
        noMatch.hidden = true;
        list.appendChild(noMatch);

        // With a list to show, the help text becomes the alternative rather than the
        // fallback: some visitors would still rather translate the page their own way.
        hasPicker = true;
        showHelp(false);
        helpToggle.addEventListener('click', function () {
            showHelp(true);
        });
    }

    // Filter rather than a dropdown: everything stays reachable by tab and by reading.
    search.addEventListener('input', function () {
        const term = search.value.trim().toLowerCase();
        let matches = 0;

        list.querySelectorAll('.language-dialog__group').forEach(function (section) {
            let visible = 0;
            section.querySelectorAll('li').forEach(function (item) {
                const hit = !term || item.firstChild.getAttribute('data-search').indexOf(term) !== -1;
                item.hidden = !hit;
                if (hit) {
                    visible++;
                }
            });
            section.hidden = visible === 0;
            matches += visible;
        });

        const noMatch = list.querySelector('.language-dialog__nomatch');
        if (noMatch) {
            noMatch.hidden = matches > 0;
        }
    });

    // Placeholders are attributes, not text nodes, but the search field is the most
    // prominent piece of text on the site - leaving it in Norwegian looks half-done.
    function collectPlaceholders() {
        return Array.from(document.querySelectorAll('input[placeholder], textarea[placeholder]'))
            .filter(function (el) {
                return el.placeholder.trim().length > 1 && !el.closest(SKIP_SELECTOR);
            });
    }

    function collectTextNodes() {
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
            acceptNode: function (node) {
                if (!node.nodeValue || node.nodeValue.trim().length < 2) {
                    return NodeFilter.FILTER_REJECT;
                }
                const parent = node.parentElement;
                if (!parent || SKIP_TAGS.indexOf(parent.tagName) !== -1) {
                    return NodeFilter.FILTER_REJECT;
                }
                if (parent.closest(SKIP_SELECTOR)) {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            }
        });

        const nodes = [];
        let node;
        while ((node = walker.nextNode())) {
            nodes.push(node);
        }
        return nodes;
    }

    function restore() {
        if (!originals) {
            return;
        }
        originals.forEach(function (entry) {
            entry.node.nodeValue = entry.text;
        });
        originalPlaceholders.forEach(function (entry) {
            entry.element.placeholder = entry.text;
        });
        document.documentElement.setAttribute('lang', sourceLanguage);
    }

    function markCurrent(code) {
        current = code;
        list.querySelectorAll('[data-language]').forEach(function (button) {
            if (button.getAttribute('data-language') === code) {
                button.setAttribute('aria-current', 'true');
            } else {
                button.removeAttribute('aria-current');
            }
        });

        // The header button carries the state - a visitor should see at a glance that they
        // are reading a machine translation - but only as a change of appearance.
        const translated = code !== sourceLanguage;

        openButton.classList.toggle('is-translated', translated);
        offButton.hidden = !translated;

        showDisclaimer(translated);
    }

    // The header shrinks once the page is scrolled, and grows back with a transition. Its
    // live height is therefore only the full one at the top and after that transition has
    // finished - measuring mid-flight leaves the bar tucked under the header. So remember
    // the full height and only refresh it when the header has settled at the top.
    let fullHeaderHeight = 0;

    function measureHeader() {
        const header = document.querySelector('header');

        if (!header) {
            return 0;
        }
        if (window.scrollY === 0) {
            fullHeaderHeight = header.offsetHeight;
        }
        return fullHeaderHeight || header.offsetHeight;
    }

    // The header is fixed at desktop widths, so a bar placed at the top of the content
    // would sit behind it. Offset it below the header instead and push the body down by
    // its height - fixed elements ignore that padding, so the header itself does not move.
    function showDisclaimer(show) {
        if (!disclaimer) {
            return;
        }

        const header = document.querySelector('header');
        const headerIsFixed = header && window.getComputedStyle(header).position === 'fixed';

        disclaimer.hidden = !show;

        if (show && headerIsFixed) {
            disclaimer.classList.add('language-disclaimer--offset');
            disclaimer.style.top = measureHeader() + 'px';
            document.body.style.paddingTop = disclaimer.offsetHeight + 'px';
        } else {
            disclaimer.classList.remove('language-disclaimer--offset');
            disclaimer.style.top = '';
            document.body.style.paddingTop = '';
        }
    }

    function repositionDisclaimer() {
        if (disclaimer && !disclaimer.hidden) {
            showDisclaimer(true);
        }
    }

    measureHeader();
    window.addEventListener('resize', repositionDisclaimer);

    // Back at the top the header animates to full height, so wait for that to finish
    // before trusting it. transitionend covers the normal case; the timeout covers a
    // header that resizes without a transition, or one whose transition never fires.
    const headerElement = document.querySelector('header');

    if (headerElement) {
        headerElement.addEventListener('transitionend', function () {
            if (window.scrollY === 0) {
                repositionDisclaimer();
            }
        });
    }

    let settleTimer = null;
    window.addEventListener('scroll', function () {
        if (window.scrollY !== 0) {
            return;
        }
        clearTimeout(settleTimer);
        settleTimer = setTimeout(repositionDisclaimer, 400);
    }, { passive: true });

    function setWorking(working) {
        if (working) {
            dialog.setAttribute('aria-busy', 'true');
            openButton.classList.add('is-working');
        } else {
            dialog.removeAttribute('aria-busy');
            openButton.classList.remove('is-working');
        }
    }

    async function translatePage(target) {
        const run = ++generation;

        // Turning translation off (back to the source language) needs no API, so it is
        // immediate. The bumped generation stops any run still in flight.
        if (target === sourceLanguage) {
            if (!originals) {
                originals = collectTextNodes().map(function (node) {
                    return { node: node, text: node.nodeValue };
                });
                originalPlaceholders = collectPlaceholders().map(function (element) {
                    return { element: element, text: element.placeholder };
                });
            }
            markCurrent(target);
            remember(target);
            restore();
            setWorking(false);
            setStatus(null);
            close();
            return;
        }

        if (!originals) {
            originals = collectTextNodes().map(function (node) {
                return { node: node, text: node.nodeValue };
            });
            originalPlaceholders = collectPlaceholders().map(function (element) {
                return { element: element, text: element.placeholder };
            });
        }

        setWorking(true);
        setStatus('preparing');

        try {
            restore();

            const translator = await Translator.create({
                sourceLanguage: sourceLanguage,
                targetLanguage: target,
                monitor: function (m) {
                    m.addEventListener('downloadprogress', function () {
                        setStatus('downloading');
                    });
                }
            });

            if (run !== generation) {
                return;
            }

            // Only now, once the model is ready: mark the UI and remember the choice.
            // Before this a failed download would leave the button looking translated with
            // nothing translated, and the failed language would stick around across pages.
            markCurrent(target);
            remember(target);

            // Getting here can mean waiting for a language model to download, and the
            // dialog is the only place that says so - closing before the words start
            // changing would leave the visitor with nothing to look at.
            setStatus('translating');
            close();

            // A few nodes at a time, so the page fills in as we go instead of freezing.
            await inBatches(originals, async function (entry) {
                if (run !== generation) {
                    return;
                }
                try {
                    const text = await translator.translate(entry.text);
                    if (run === generation) {
                        entry.node.nodeValue = text;
                    }
                } catch (e) {
                    // Leave the node as it was - one failure should not stop the rest.
                }
            });

            await inBatches(originalPlaceholders, async function (entry) {
                if (run !== generation) {
                    return;
                }
                try {
                    const text = await translator.translate(entry.text);
                    if (run === generation) {
                        entry.element.placeholder = text;
                    }
                } catch (e) {
                    // Keep the original placeholder.
                }
            });

            if (run !== generation) {
                return;
            }

            document.documentElement.setAttribute('lang', target);
            setStatus(null);
        } catch (e) {
            if (run !== generation) {
                return;
            }
            // A failure before the translation started leaves the dialog open; one after
            // means bringing it back, rather than failing in silence.
            setStatus('failed');
            if (!dialog.open) {
                open();
            }
        } finally {
            // A superseded run must not clear the state of the run that replaced it.
            if (run === generation) {
                setWorking(false);
            }
        }
    }
})();
